import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Link, Navigate } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, Button, Container, IconButton, Avatar, Menu, MenuItem } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { auth } from './utils/auth';
import axios from 'axios';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Founder from './pages/Founder';
import Resume from './pages/Resume';
import SignUp from './pages/SignUp';
import Login from './pages/Login';
import AskAI from './pages/AskAI';
import Settings from './pages/Settings';
import NotesGenerator from './pages/NotesGenerator';
import MindMap from './pages/MindMap';
import VoiceToNotes from './pages/voice-to-notes';
import Flashcards from './pages/Flashcards';
import AIPracticeQuestions from './pages/AIPracticeQuestions';
import SamplePaper from './pages/SamplePaper';
import ScheduleMaker from './pages/ScheduleMaker';
import DiagramGenerator from './pages/DiagramGenerator';
// ExamMode removed
import { generateContent } from './utils/ai';

function App() {
  // State for Extra footer menu
  const [extraMenuAnchor, setExtraMenuAnchor] = useState(null);
  // Banner dismiss state for disclaimer
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem('ixstudy_banner_dismissed') === '1';
    }
    return false;
  });
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverStatus, setServerStatus] = useState('');

  // Initialize darkMode based on system preference, then check localStorage
  const [darkMode, setDarkMode] = useState(() => {
    const savedPreference = localStorage.getItem('darkMode');
    if (savedPreference !== null) {
      return savedPreference === 'true';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for system color scheme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only update if user hasn't set a preference
      if (localStorage.getItem('darkMode') === null) {
        setDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Update data-theme attribute when darkMode changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const checkServerStatus = async () => {
      try {
        const messages = [{ role: 'user', content: 'Test' }];
        await generateContent(messages, { max_tokens: 1, temperature: 0.7 });
        setServerStatus('Server Online');
      } catch (error) {
        console.error('Error checking server status:', error);
        setServerStatus('Unable to connect to server. Please try again later.');
      }
    };

    const initializeAuth = async () => {
      try {
        console.log('Initializing auth...');
        const currentUser = await auth.getCurrentUser();
        console.log('Auth initialization result:', currentUser);
        setUser(currentUser);
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        console.log('Setting loading to false');
        setLoading(false);
      }
    };

    checkServerStatus();
    initializeAuth();
  }, []);

  const handleLogout = () => {
    auth.logout();
    setUser(null);
    navigate('/');
  };


  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
    },
  });

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
    // Manually trigger favicon update
    const event = new Event('storage');
    window.dispatchEvent(event);
  };



  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
      <AppBar position="fixed">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Link to="/dashboard" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <img
                  src="/tixo-logo.svg"
                  alt="IX Study Logo"
                  style={{ height: '50px' }}
                />
              </Box>
              <Typography variant="h6" component="div" sx={{ ml: 2 }}>
                IX-Study
              </Typography>
            </Link>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!user ? (
              <>
                <Button color="inherit" component={Link} to="/login">
                  Login
                </Button>
                <Button color="inherit" component={Link} to="/signup">
                  Sign Up
                </Button>
              </>
            ) : (
              <>
                <Avatar
                  src={user?.profilePicture}
                  alt={user?.email}
                  sx={{ width: 32, height: 32, mr: 1, cursor: 'pointer' }}
                  onClick={() => navigate('/settings')}
                />
                <Button color="inherit" onClick={handleLogout}>
                  Logout
                </Button>
              </>
            )}

            <IconButton
              color="inherit"
              edge="end"
              href="/settings"
              sx={{ ml: 1 }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar /> {/* Spacer for fixed AppBar */}
      <Container maxWidth="lg" sx={{ mt: 4, pb: !user ? 4 : 0 }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/founder" element={<Founder />} />
          <Route path="/resume" element={<Resume />} />
          <Route path="/signup" element={<SignUp setUser={setUser} />} />
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/dashboard" element={<Dashboard serverStatus={serverStatus} />} />
          <Route path="/askai" element={<AskAI />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/notes-generator" element={<NotesGenerator />} />
          <Route path="/mindmap" element={<MindMap />} />
          <Route path="/voice-to-notes" element={<VoiceToNotes />} />
          <Route path="/flashcards" element={<Flashcards />} />
          <Route path="/practice-questions" element={<AIPracticeQuestions />} />
          <Route path="/sample-paper" element={<SamplePaper />} />
          <Route path="/schedule" element={<ScheduleMaker />} />
          <Route path="/diagram-generator" element={<DiagramGenerator />} />
          <Route path="/exam-mode" element={<Navigate to="/practice-questions" state={{ showExamModeNotice: true }} />} />
        </Routes>
      </Container>

      {/* Disclaimer Banner for non-signed-in users */}
      {!user && !bannerDismissed && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 3000,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          py: 1.5,
          textAlign: 'center',
          boxShadow: 3,
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            By using this site, you agree to our{' '}
            <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</Link>{' '}and{' '}
            <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</Link>.
          </Typography>
          <Button
            aria-label="Close disclaimer"
            onClick={() => {
              setBannerDismissed(true);
              if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('ixstudy_banner_dismissed', '1');
            }}
            sx={{ minWidth: 0, px: 1, color: 'text.secondary', fontSize: 20 }}
          >
            ×
          </Button>
        </Box>
      )}
    </Box>
    </ThemeProvider>
  );
}

export default App;